/**
 * PortalAccessControlsCard (Story 14.4 AC #7)
 *
 * Reusable card used by CreateShareLinkSheet + EditShareLinkSheet to let the
 * agency hide ad-spend / cost-efficiency metrics and specific connected
 * integrations from a shared portal link.
 *
 * Sentinel convention (D22 / D25 / D26):
 *  - "__cost_efficiency__"  — Cost/CPC/CPM/spend quick toggle. Sentinel is
 *                             expanded at filter time against
 *                             `COST_EFFICIENCY_METRIC_IDS` in the service layer.
 *  - Individual cost metrics ("spend", "cpc", etc.) are deliberately NOT
 *    surfaced in the advanced list; they are owned by the quick toggle. If
 *    the user also checks a specific metric in the advanced list while the
 *    sentinel is on, both are counted in the summary line for transparency.
 *
 * The card is state-less internally: it reads the current arrays from props
 * and emits `onChange({ hiddenMetrics, hiddenIntegrations })` on every change.
 * This keeps the Sheet the single source of truth for dirty-detection.
 */

import { useMemo, useState } from "react";
import { Switch, Input } from "@provenleap/ui";
import { Info, CaretDown, CaretUp } from "@phosphor-icons/react";
import { useClientIntegrations } from "../../hooks/useClientIntegrations";
import { useAvailableMetrics } from "../../hooks/useAvailableMetrics";
import { PICKER_PLATFORMS } from "../../lib/integrations";
import {
  PORTAL_HIDDEN_METRIC_SENTINELS,
  PORTAL_COST_EFFICIENCY_METRIC_IDS,
} from "../../lib/portalLinksApi";

interface PortalAccessControlsCardProps {
  clientId: string;
  hiddenMetrics: string[];
  hiddenIntegrations: string[];
  onChange: (next: {
    hiddenMetrics: string[];
    hiddenIntegrations: string[];
  }) => void;
  disabled?: boolean;
}

const PLATFORM_DISPLAY_BY_KEY = new Map(
  PICKER_PLATFORMS.map((p) => [p.platform, p.displayName])
);

function platformDisplayName(platform: string): string {
  return PLATFORM_DISPLAY_BY_KEY.get(platform) ?? platform;
}

function pluralize(n: number, singular: string, plural: string): string {
  return n === 1 ? singular : plural;
}

/**
 * Slug-form a category label for safe use in `data-testid` and CSS
 * selectors: lowercase, non-alphanumeric runs collapsed to "-", trimmed.
 * Handles categories with spaces, ampersands, or unicode gracefully.
 */
function categorySlug(category: string): string {
  return category.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
}

export function PortalAccessControlsCard({
  clientId,
  hiddenMetrics,
  hiddenIntegrations,
  onChange,
  disabled = false,
}: PortalAccessControlsCardProps) {
  const { connectedPlatforms: rawConnectedPlatforms, isLoading: integrationsLoading } =
    useClientIntegrations(clientId);
  const { allMetrics, isLoading: metricsLoading } =
    useAvailableMetrics(clientId);

  // R3 fix R3-1: dedupe in case a client has multiple integrations of the same
  // type (e.g. two Facebook Ads accounts). Prevents React duplicate-key warnings
  // and duplicate chip rendering.
  const connectedPlatforms = useMemo(
    () => Array.from(new Set(rawConnectedPlatforms)),
    [rawConnectedPlatforms]
  );

  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState<string | null>(null);

  const costEfficiencyHidden = hiddenMetrics.includes(
    PORTAL_HIDDEN_METRIC_SENTINELS.COST_EFFICIENCY
  );

  // R1 fix H3: `emit` dedupes both arrays before calling onChange so rapid
  // double-clicks (touch, keyboard repeat) that read the same props snapshot
  // cannot produce duplicate entries in the parent's state.
  const emit = (nextHiddenMetrics: string[], nextHiddenIntegrations: string[]) =>
    onChange({
      hiddenMetrics: Array.from(new Set(nextHiddenMetrics)),
      hiddenIntegrations: Array.from(new Set(nextHiddenIntegrations)),
    });

  function toggleSentinel(sentinel: string, checked: boolean) {
    if (disabled) return;
    const next = checked
      ? [...hiddenMetrics.filter((m) => m !== sentinel), sentinel]
      : hiddenMetrics.filter((m) => m !== sentinel);
    emit(next, hiddenIntegrations);
  }

  function toggleIntegration(platform: string) {
    if (disabled) return;
    const has = hiddenIntegrations.includes(platform);
    const next = has
      ? hiddenIntegrations.filter((p) => p !== platform)
      : [...hiddenIntegrations, platform];
    emit(hiddenMetrics, next);
  }

  function toggleMetric(metricId: string) {
    if (disabled) return;
    if (metricId === PORTAL_HIDDEN_METRIC_SENTINELS.COST_EFFICIENCY) {
      // Defense in depth — sentinels are never rendered in the advanced list,
      // but if they somehow arrive here we leave the quick-toggle state alone.
      return;
    }
    const has = hiddenMetrics.includes(metricId);
    const next = has
      ? hiddenMetrics.filter((m) => m !== metricId)
      : [...hiddenMetrics, metricId];
    emit(next, hiddenIntegrations);
  }

  // ── Derived collections ────────────────────────────────────────────────

  // Metrics visible in the advanced list: all metrics from all connected
  // integrations, minus the sentinels (D26), minus the cost-efficiency
  // members when the cost sentinel is active (R1 fix H4 — prevents ambiguous
  // "already hidden by toggle, but also checkable here" state), and
  // de-duplicated by key.
  const visibleMetrics = useMemo(() => {
    const costSet = new Set(PORTAL_COST_EFFICIENCY_METRIC_IDS);
    const seen = new Set<string>();
    const unique: Array<{
      key: string;
      label: string;
      category: string;
      platform: string;
    }> = [];
    for (const m of allMetrics) {
      if (m.key === PORTAL_HIDDEN_METRIC_SENTINELS.COST_EFFICIENCY) {
        continue;
      }
      if (costEfficiencyHidden && costSet.has(m.key)) continue;
      if (seen.has(m.key)) continue;
      seen.add(m.key);
      unique.push({
        key: m.key,
        label: m.label,
        category: m.category ?? "Other",
        platform: m.platform,
      });
    }
    return unique;
  }, [allMetrics, costEfficiencyHidden]);

  const availableCategories = useMemo(() => {
    const set = new Set(visibleMetrics.map((m) => m.category));
    return Array.from(set).sort();
  }, [visibleMetrics]);

  const filteredMetrics = useMemo(() => {
    const q = search.trim().toLowerCase();
    return visibleMetrics.filter((m) => {
      if (activeCategory && m.category !== activeCategory) return false;
      if (q.length > 0 && !m.label.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [visibleMetrics, search, activeCategory]);

  // R1 fix M5: expand the summary-line count so agencies see the real number
  // of metrics the client won't see. Count is: non-sentinel metrics already in
  // `hiddenMetrics` + cost-efficiency members (when the sentinel is active).
  const effectiveHiddenMetricsCount = (() => {
    let count = 0;
    const costSet = new Set(PORTAL_COST_EFFICIENCY_METRIC_IDS);
    for (const m of hiddenMetrics) {
      if (m === PORTAL_HIDDEN_METRIC_SENTINELS.COST_EFFICIENCY) continue;
      // Skip individual cost members already covered by the sentinel to avoid
      // double-counting (fixes R1-H4's "double select" concern alignment)
      if (costEfficiencyHidden && costSet.has(m)) continue;
      count += 1;
    }
    if (costEfficiencyHidden) {
      count += PORTAL_COST_EFFICIENCY_METRIC_IDS.length;
    }
    return count;
  })();
  const hiddenIntegrationCount = hiddenIntegrations.length;

  return (
    <div className="space-y-4 pt-4 border-t-[0.5px] border-input">
      <div className="space-y-1">
        <label className="text-sm font-medium">Visibility</label>
        <p className="text-xs text-muted-foreground">
          Hide specific metrics or integrations from this share link.
        </p>
      </div>

      {/* ── Quick toggles — switch rows, no separate card ──────────────── */}
      <div className="space-y-2.5">
        <div className="flex items-center justify-between gap-3">
          <label
            htmlFor="toggle-cost-efficiency"
            className="flex items-center gap-1.5 text-sm cursor-pointer"
          >
            Hide ad spend & cost-efficiency
            <span
              title="Your clients won't see spend, CPC, CPM, CPA, cost per purchase, or amount spent — across all ads platforms."
              aria-label="Your clients won't see spend, CPC, CPM, CPA, cost per purchase, or amount spent — across all ads platforms."
              className="inline-flex items-center cursor-help text-muted-foreground"
            >
              <Info size={14} aria-hidden="true" />
            </span>
          </label>
          <Switch
            id="toggle-cost-efficiency"
            checked={costEfficiencyHidden}
            disabled={disabled}
            onCheckedChange={(c) =>
              toggleSentinel(
                PORTAL_HIDDEN_METRIC_SENTINELS.COST_EFFICIENCY,
                c === true
              )
            }
            aria-label="Hide ad spend and cost-efficiency metrics"
          />
        </div>

      </div>

      {/* ── Integration chips ──────────────────────────────────────────── */}
      <section
        aria-labelledby="pac-integrations-title"
        className="space-y-2"
      >
        <p
          id="pac-integrations-title"
          className="text-xs text-muted-foreground"
        >
          Hide specific integrations
        </p>

        {integrationsLoading ? (
          <p className="text-xs text-muted-foreground">Loading integrations…</p>
        ) : connectedPlatforms.length === 0 ? (
          <p className="text-xs text-muted-foreground">
            No integrations connected for this client yet.
          </p>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {connectedPlatforms.map((platform) => {
              const isHidden = hiddenIntegrations.includes(platform);
              return (
                <button
                  key={platform}
                  type="button"
                  disabled={disabled}
                  aria-pressed={isHidden}
                  aria-disabled={disabled || undefined}
                  onClick={() => toggleIntegration(platform)}
                  className={
                    "rounded-lg border-[0.5px] px-2.5 py-1 text-xs transition-colors " +
                    (isHidden
                      ? "bg-primary text-primary-foreground border-primary"
                      : "bg-background border-input hover:bg-muted/30") +
                    (disabled ? " opacity-50 cursor-not-allowed" : " cursor-pointer")
                  }
                  data-testid={`integration-chip-${platform}`}
                >
                  {platformDisplayName(platform)}
                </button>
              );
            })}
          </div>
        )}
      </section>

      {/* ── Advanced metric list — collapsible inline link ─────────────── */}
      <div className="space-y-2">
        <button
          type="button"
          onClick={() => setAdvancedOpen((v) => !v)}
          aria-expanded={advancedOpen}
          aria-controls="pac-advanced-panel"
          disabled={disabled}
          className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground cursor-pointer transition-colors"
          data-testid="advanced-toggle"
        >
          {advancedOpen ? <CaretUp size={12} /> : <CaretDown size={12} />}
          {advancedOpen ? "Hide" : "Show"} advanced metric filters
        </button>

        {advancedOpen && (
          <div id="pac-advanced-panel" className="space-y-2 pt-1">
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search metrics…"
              disabled={disabled}
              className="rounded-lg h-9 text-sm"
              data-testid="advanced-search"
            />

            {availableCategories.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {availableCategories.map((cat) => {
                  const active = activeCategory === cat;
                  return (
                    <button
                      key={cat}
                      type="button"
                      disabled={disabled}
                      aria-pressed={active}
                      aria-disabled={disabled || undefined}
                      onClick={() => {
                        if (disabled) return;
                        setActiveCategory(active ? null : cat);
                      }}
                      className={
                        "rounded-lg border-[0.5px] px-2 py-0.5 text-xs transition-colors " +
                        (active
                          ? "bg-primary text-primary-foreground border-primary"
                          : "bg-background border-input hover:bg-muted/30") +
                        (disabled ? " opacity-50 cursor-not-allowed" : " cursor-pointer")
                      }
                      data-testid={`category-chip-${categorySlug(cat)}`}
                    >
                      {cat}
                    </button>
                  );
                })}
              </div>
            )}

            <div className="max-h-56 overflow-y-auto space-y-0.5 rounded-lg border-[0.5px] border-input bg-background p-1.5">
              {metricsLoading ? (
                <p className="text-xs text-muted-foreground p-1.5">
                  Loading metrics…
                </p>
              ) : connectedPlatforms.length === 0 ? (
                <p className="text-xs text-muted-foreground p-1.5">
                  No metrics available — connect integrations first.
                </p>
              ) : filteredMetrics.length === 0 ? (
                <p className="text-xs text-muted-foreground p-1.5">
                  No metrics match your filters.
                </p>
              ) : (
                filteredMetrics.map((m) => {
                  const isChecked = hiddenMetrics.includes(m.key);
                  const id = `metric-${m.platform}-${m.key}`;
                  return (
                    <label
                      key={id}
                      htmlFor={id}
                      className={
                        "flex items-center justify-between gap-2 text-xs px-2 py-1 rounded-md " +
                        (disabled ? "opacity-50" : "hover:bg-muted/30 cursor-pointer")
                      }
                    >
                      <span className="flex items-center gap-2 min-w-0">
                        <input
                          id={id}
                          type="checkbox"
                          checked={isChecked}
                          disabled={disabled}
                          onChange={() => toggleMetric(m.key)}
                          className="rounded touch-target-sm"
                          data-testid={`metric-checkbox-${m.key}`}
                        />
                        <span className="truncate">{m.label}</span>
                      </span>
                      <span className="text-muted-foreground text-[10px] flex-shrink-0">
                        {platformDisplayName(m.platform)}
                      </span>
                    </label>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>

      {/* ── Summary line — always rendered so screen readers can announce
          state changes via aria-live; visually muted when nothing is hidden. */}
      <p
        aria-live="polite"
        className="text-xs text-muted-foreground"
        data-testid="access-controls-summary"
      >
        {effectiveHiddenMetricsCount}{" "}
        {pluralize(effectiveHiddenMetricsCount, "metric", "metrics")} and{" "}
        {hiddenIntegrationCount}{" "}
        {pluralize(hiddenIntegrationCount, "integration", "integrations")}{" "}
        selected to hide.
      </p>
    </div>
  );
}
